// Modules
import { isEmpty } from 'lodash';
import React, { Fragment, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { CellProps } from 'react-table';

// Components
import AnglerExpanded from '../../components/AnglerExpanded/AnglerExpanded';
import CutlineText from '../../components/CutlineText/CutlineText';
import Message from '../../components/Message/Message';
import ReactTable from '../../components/ReactTable/ReactTable';
import VideoPlayer, {
  IVideoPlayerContext,
  VideoPlayerContext,
} from '../../components/VideoPlayer/VideoPlayer';
import BasstrakkTableRenderRow from './BasstrakkTableRenderRow';

// Utils
import { basstrakkCatchesTop5 } from '../../util/processing';

// Config
import { CLASS_PREFIX, DEFAULT_STRING_NO_TABLE_RESULTS } from '../../config';

// Types
import {
  AdvertisementVariant,
  IBasstrakkAngler,
  ICutline,
  IDataProps,
} from '../../types';

// Table Columns
import AdWrapper from '../../components/Advertisement/AdWrapper';
import { TabProvider } from '../../components/Context';
import { getBasstrakkColumns } from './BasstrakkColumns';
// import TabContext from '../../components/Context';

const videoPlayerId = 'bm-video-player';

interface IBasstrakkTable {
  cutlines: ICutline[];
  data: any[];
  tabLabel: string;
  tabClass: string;
  tid: number;
  tournamentSeries: string;
}

/**
 * Basstrakk Standings
 */
const BasstrakkTable = (props: IBasstrakkTable) => {
  const { cutlines: basstrakkCutlines, data, tournamentSeries } = props;

  // Tab context
  // const { tab } = useContext(TabContext);

  // Angler.
  const [cutlines] = useState<ICutline[]>(
    !isEmpty(basstrakkCutlines) ? basstrakkCutlines : []
  );
  // const [aoyStandings] = useState(data);

  // Video realted.
  const [videoId, setVideoId] = useState<number | null>(null);
  const [videoPlayerNode, setVideoPlayerNode] = useState(
    document.getElementById(videoPlayerId) || null
  );
  const [hasVideoPlayer, setHasVideoPlayer] = useState<boolean>(false);
  const [isVideoPlaying] = useState<boolean>(false);

  // const [bannerIndex, setBannerIndex] = useState(1);

  const { view = '' } = Object.fromEntries(new URLSearchParams(location.search));

  // Get the Basstrakk Table Columns
  const headerColumns = useMemo(() => getBasstrakkColumns(tournamentSeries, view), [tournamentSeries, view]);

  // Get the AOY rank of the angler if there is one
  // The `tms` id is returned as a string and the `rank` is a number
  // const getAoyRank = (angler: IBasstrakkAngler) => {
  //   // @todo Replace aoyStandings with allRows.AoyStanding
  //   const current = aoyStandings.filter(
  //     a => Number(a.tms) === angler.FISHERMENID
  //   );
  //   if (current.length === 0) {
  //     return 0;
  //   }
  //   return Number(current[0].rank);
  // };

  /**
   * Injects a div at the end of the document.body so that the React Portal can be created
   */
  const onVideoSelect = (selectedVideoId: number) => {
    let videoPlayerNodeById = document.getElementById(
      videoPlayerId
    ) as HTMLDivElement;

    if (!videoPlayerNodeById) {
      videoPlayerNodeById = document.createElement('div');
      videoPlayerNodeById.id = videoPlayerId;
      document.body.appendChild(videoPlayerNodeById);
    }

    setVideoId(selectedVideoId);
    setVideoPlayerNode(videoPlayerNodeById);
    setHasVideoPlayer(true);
  };

  const setContext = (newContext: Partial<IVideoPlayerContext>) => {
    const {
      videoId: videoIdFromContext,
      hasVideoPlayer: hasVideoPlayerFromContext,
    } = newContext;
    if (videoIdFromContext) {
      onVideoSelect(videoIdFromContext);
      return;
    }

    // Key must be false, if it's undefined skip
    if (hasVideoPlayerFromContext === false) {
      setHasVideoPlayer(false);
      return;
    }
  };

  /**
   * Use to determine when a banner ad should display
   *
   * This does not include banner ads that should display with cutlines
   * @param index
   */
  const shouldRenderBanner = index => {
    // @todo: Add config in Drupal to allow passing these hardcoded values to
    // the frontend
    const targetRows = [30, 70];

    if (index !== 0 && targetRows.includes(index)) {
      return true;
    }
    return false;
  };

  // Renders a row in the table
  const renderRow = ({ row, index }: CellProps<IDataProps>) => {
    return <BasstrakkTableRenderRow row={row} index={index} />;
  };

  // Allows rendering an "expanded" row below a default table row.
  const renderExpanded = ({ row, index }: CellProps<IDataProps>) => {
    const { original: angler } = row;
    return (
      <AnglerExpanded
        anglerId={angler.FISHERMENID}
        anglerName={angler.FISHERMAN_PHONENAME}
        catchData={basstrakkCatchesTop5(
          angler.FISHERMENID,
          row.original.Catches
        )}
        count={index}
      />
    );
  };

  const renderRowSeparator = ({ row, index }: CellProps<IBasstrakkAngler>) => {
    return (
      <>
        {cutlines.map(cutline => {
          if (cutline.cutlineValue === index + 1) {
            return (
              <Fragment key={cutline.cutlineValue}>
                <div
                  className={`${
                    row.isExpanded ? 'cutline cutline--expanded' : 'cutline'
                  }`}
                  data-testid="cutline-row"
                >
                  <div style={{ width: '100%' }} className="cutline-text">
                    <CutlineText text={cutline.cutlineText} />
                  </div>
                </div>
                <AdWrapper
                  count={cutline.cutlineValue}
                  variant={AdvertisementVariant.CUTLINE}
                />
              </Fragment>
            );
          }
        })}
        {shouldRenderBanner(index + 1) && (
          <AdWrapper
            count={index + 1}
            variant={AdvertisementVariant.BASSTRAKK}
          />
        )}
      </>
    );
  };

  // We have a default message when there is no data
  const shouldShow = isEmpty(data);

  return (
    <>
      {shouldShow ? (
        <Message
          message={DEFAULT_STRING_NO_TABLE_RESULTS}
          customClass="no-results-message"
          shouldShow={shouldShow}
        />
      ) : (
        <>
          <TabProvider value={{ tab: 'basstrakk' }}>
            <VideoPlayerContext.Provider
              value={{
                hasVideoPlayer,
                isVideoPlaying,
                setContext,
                videoId,
              }}
            >
              <ReactTable
                tableName="basstrakk"
                columns={headerColumns}
                data={data}
                renderExpanded={renderExpanded}
                renderRow={renderRow}
                renderRowSeparator={renderRowSeparator}
                classPrefix={CLASS_PREFIX}
              />
              {hasVideoPlayer &&
                videoPlayerNode &&
                ReactDOM.createPortal(
                  <VideoPlayer hasAutoPlay />,
                  videoPlayerNode
                )}
            </VideoPlayerContext.Provider>
          </TabProvider>
        </>
      )}
    </>
  );
};

export default BasstrakkTable;
